import React, { useState, useEffect, useRef } from "react";
import { menuItems } from "./Menu"; // Assuming the `menuItems` data is still coming from the `Menu.js` file
import logoBlack from "../../Image/logo/kia-central-black.png";
import logoWhite from "../../Image/kialogo.png";
import CloseIcon from "@mui/icons-material/Close";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation } from "react-router-dom";
import { HEADER_HEIGHT, SCROLL_HIDE_THRESHOLD } from "../../Header/Components/Header.js";
import "./Nav.css";

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { pathname } = useLocation();
  const desktopMenuItems = menuItems.filter((item) => item.title !== "Home");
  const isHome = pathname === "/";
  // Transparent + white text only while sitting over the home hero and
  // unscrolled; everywhere else (and once scrolled) it's a solid white bar.
  const isTransparent = isHome && !scrolled;
  const logoImage = isTransparent ? logoWhite : logoBlack;

  const toggleDrawer = (open) => {
    setDrawerOpen(open);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > SCROLL_HIDE_THRESHOLD);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // MenuItems component logic
  const MenuItems = ({ items, depthLevel }) => {
    const [dropdown, setDropdown] = useState(false);
    const ref = useRef();

    useEffect(() => {
      const handler = (event) => {
        if (dropdown && ref.current && !ref.current.contains(event.target)) {
          setDropdown(false);
        }
      };

      document.addEventListener("mousedown", handler);
      document.addEventListener("touchstart", handler);

      return () => {
        document.removeEventListener("mousedown", handler);
        document.removeEventListener("touchstart", handler);
      };
    }, [dropdown]);

    // Close the submenu the moment the page scrolls, so it doesn't float
    // out of place; it reopens normally on the next hover.
    useEffect(() => {
      if (!dropdown) return;
      const handleScroll = () => setDropdown(false);
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }, [dropdown]);

    const onMouseEnter = () => {
      if (window.innerWidth > 960) {
        setDropdown(true);
      }
    };

    const onMouseLeave = () => {
      if (window.innerWidth > 960) {
        setDropdown(false);
      }
    };

    const isActiveLeaf = items.path === pathname;
    const isActiveParent =
      items.submenu?.some((sub) => sub.path === pathname) ?? false;

    return (
      <li
        className="menu-items"
        ref={ref}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}>
        {items.submenu ? (
          <>
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={dropdown ? "true" : "false"}
              className={isActiveParent ? "active" : ""}
              onClick={() => setDropdown((prev) => !prev)}>
              {items.title}{" "}
              {depthLevel > 0 ? (
                <span>&raquo;</span>
              ) : (
                <span className="arrow" />
              )}
            </button>
            <ul className={`dropdown ${dropdown ? "show" : ""}`}>
              {items.submenu.map((submenu, index) => (
                <MenuItems
                  items={submenu}
                  key={index}
                  depthLevel={depthLevel + 1}
                />
              ))}
            </ul>
          </>
        ) : (
          <Link
            to={items.path}
            className={`menu-link ${isActiveLeaf ? "active" : ""}`}
            onClick={() => setDrawerOpen(false)} // Close drawer when menu item is clicked
          >
            {items.title}
          </Link>
        )}
      </li>
    );
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: isTransparent ? "transparent" : "#ffffff",
        boxShadow: isTransparent ? "none" : "0 2px 10px rgba(0,0,0,0.08)",
        borderBottom: isTransparent ? "1px solid transparent" : "1px solid #eee",
        marginTop: scrolled ? "0px" : `${HEADER_HEIGHT}px`,
        zIndex: theme.zIndex.drawer + 1,
        transition:
          "background-color 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, margin-top 0.3s ease",
      }}>
      <Toolbar
        className={isTransparent ? "nav-transparent" : ""}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
         
          padding: { xs: "0 12px", md: "0 24px" },
        }}>
        {/* Logo Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: isMobile ? "160px" : "180px",
            height: "100%",
          }}>
          <a
            href="/"
            onClick={() => setDrawerOpen(false)}
            style={{ display: "flex" }}>
            <img
              src={logoImage}
              alt="Company Logo"
              width="140"
              height="50"
              style={{ objectFit: "contain" }}
            />
          </a>
        </Box>

        {/* Mobile View: Drawer and Menu Icon */}
        {isMobile ? (
          <>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setDrawerOpen(!drawerOpen)}
              aria-label="menu">
              {drawerOpen ? (
                <CloseIcon sx={{ color: "#D62E31" }} />
              ) : (
                <MenuIcon sx={{ color: "#D62E31" }} />
              )}
            </IconButton>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{
                sx: {
                  width: "72%",
                  maxWidth: "320px",
                  backgroundColor: "#ffffff",
                  marginTop: `${HEADER_HEIGHT + 56}px`,
                },
              }}>
              <Box>
                <Box sx={{ display: "flex", justifyContent: "center", py: 2.5 }}>
                  <a href="/" onClick={() => setDrawerOpen(false)}>
                    <img
                      src={logoBlack}
                      alt="Company Logo"
                      width="130"
                      height="46"
                      style={{ objectFit: "contain" }}
                    />
                  </a>
                </Box>

                <Divider sx={{ backgroundColor: "#eee" }} />
                <Box>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {menuItems.map((menu, index) => (
                      <React.Fragment key={index}>
                        <MenuItems items={menu} depthLevel={0} />
                        <Divider sx={{ backgroundColor: "#eee" }} />
                      </React.Fragment>
                    ))}
                  </ul>
                </Box>
              </Box>
            </Drawer>
          </>
        ) : (
          <>
            {/* Desktop View: Horizontal Menu */}
            <Box sx={{ display: "flex" }}>
              <ul
                style={{
                  display: "flex",
                  alignItems: "center",
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                }}>
                {desktopMenuItems.map((menu, index) => (
                  <MenuItems items={menu} key={index} depthLevel={0} />
                ))}
              </ul>
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
